//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { AccountRole, type AccountUuid, type PersonId, type Ref } from '@hcengineering/core'
import document from './plugin'
import { type Document, type DocumentGeneralAccess, type DocumentRole } from './types'

/*
  Who may do what with a document, Google Docs style. One set of rules for
  the server (transactor and collaborator) and the browser.

  - The teamspace is the shared folder: only its members see its pages.
  - A page's owner is whoever created it or one of its parents; teamspace
    owners and Superadmins own every page. Owners may do anything.
  - Everyone else gets the best of: the roles shared with them on the page or
    a parent, and the page's general access (the nearest page up the tree
    that sets one; editor when none does). 'restricted' gives nothing.
*/

/** What one person may do with a page, weakest first. */
export type DocumentAccessLevel = 'none' | DocumentRole | 'owner'

const levelRank: Record<DocumentAccessLevel, number> = {
  none: 0,
  viewer: 1,
  commenter: 2,
  editor: 3,
  owner: 4
}

/** The fields of a page the rules read. */
export type AccessDoc = Pick<
Document,
'_id' | 'parent' | 'createdBy' | 'generalAccess' | 'sharedWith' | 'editorsCanShare' | 'viewersCanExport'
>

/** The fields to fetch for `AccessDoc`. */
export const accessDocProjection = {
  _id: 1,
  parent: 1,
  space: 1,
  createdBy: 1,
  generalAccess: 1,
  sharedWith: 1,
  editorsCanShare: 1,
  viewersCanExport: 1
} as const

export interface AccessSpace {
  members: AccountUuid[]
  owners?: AccountUuid[]
  /** Set on a tracker project's own teamspace (who ran the project at the last sync). */
  projectOwners?: AccountUuid[]
}

export interface AccessAccount {
  uuid: AccountUuid
  role: AccountRole
  socialIds: PersonId[]
}

/** The page and its parents, the page first. Stops at a missing parent or a loop. */
export function accessChain (doc: AccessDoc, byId: (_id: Ref<Document>) => AccessDoc | undefined): AccessDoc[] {
  const chain: AccessDoc[] = [doc]
  const seen = new Set<Ref<Document>>([doc._id])
  let parent = doc.parent
  while (parent != null && parent !== document.ids.NoParent && !seen.has(parent)) {
    const next = byId(parent)
    if (next === undefined) break
    chain.push(next)
    seen.add(parent)
    parent = next.parent
  }
  return chain
}

/** The general access in force for a chain: the nearest page that sets one, else editor. */
export function generalAccessOf (chain: AccessDoc[]): DocumentGeneralAccess {
  for (const doc of chain) {
    if (doc.generalAccess != null) return doc.generalAccess
  }
  return 'editor'
}

/** A yes/no sharing setting in force for a chain: the nearest page that sets it, else yes. */
export function settingOf (chain: AccessDoc[], key: 'editorsCanShare' | 'viewersCanExport'): boolean {
  for (const doc of chain) {
    const value = doc[key]
    if (value != null) return value
  }
  return true
}

/** The roles shared on a chain's pages, the strongest per person. */
export function sharesOf (chain: AccessDoc[]): Map<AccountUuid, DocumentRole> {
  const result = new Map<AccountUuid, DocumentRole>()
  for (const doc of chain) {
    for (const [account, role] of Object.entries(doc.sharedWith ?? {}) as Array<[AccountUuid, DocumentRole]>) {
      const prev = result.get(account)
      if (prev === undefined || levelRank[role] > levelRank[prev]) result.set(account, role)
    }
  }
  return result
}

export function isSuperadmin (account: Pick<AccessAccount, 'role'>): boolean {
  return account.role === AccountRole.Owner
}

/**
 * Whether the account owns the page: Superadmin, teamspace owner, or creator
 * of it or a parent. In a project's teamspace the workspace's admins own the
 * pages too, as they run every project with its owners and team lead (the
 * owner, 2026-10-27: an admin gets the same sharing controls as the lead).
 */
export function isDocumentOwner (chain: AccessDoc[], space: AccessSpace, account: AccessAccount): boolean {
  if (isSuperadmin(account)) return true
  if (space.owners?.includes(account.uuid) === true) return true
  if (space.projectOwners !== undefined && account.role === AccountRole.Maintainer) return true
  const mine = new Set<PersonId>(account.socialIds)
  return chain.some((doc) => doc.createdBy != null && mine.has(doc.createdBy))
}

/** What the account may do with the first page of the chain. */
export function documentAccess (chain: AccessDoc[], space: AccessSpace, account: AccessAccount): DocumentAccessLevel {
  if (isDocumentOwner(chain, space, account)) return 'owner'
  if (!space.members.includes(account.uuid)) return 'none'
  const general = generalAccessOf(chain)
  const base: DocumentAccessLevel = general === 'restricted' ? 'none' : general
  const shared = sharesOf(chain).get(account.uuid)
  return shared !== undefined && levelRank[shared] > levelRank[base] ? shared : base
}

export function atLeast (level: DocumentAccessLevel, required: DocumentAccessLevel): boolean {
  return levelRank[level] >= levelRank[required]
}

export function canViewDocument (level: DocumentAccessLevel): boolean {
  return atLeast(level, 'viewer')
}

export function canCommentDocument (level: DocumentAccessLevel): boolean {
  return atLeast(level, 'commenter')
}

export function canEditDocument (level: DocumentAccessLevel): boolean {
  return atLeast(level, 'editor')
}

/** Adding people and changing their roles and the general access. */
export function canShareDocument (level: DocumentAccessLevel, chain: AccessDoc[]): boolean {
  return level === 'owner' || (level === 'editor' && settingOf(chain, 'editorsCanShare'))
}

/** Deleting the page, and the two yes/no sharing settings: owners only. */
export function canManageDocument (level: DocumentAccessLevel): boolean {
  return level === 'owner'
}

/** Downloading and printing: editors always, viewers and commenters unless the owner turned it off. */
export function canExportDocument (level: DocumentAccessLevel, chain: AccessDoc[]): boolean {
  return canEditDocument(level) || (canViewDocument(level) && settingOf(chain, 'viewersCanExport'))
}

/** The document fields that are sharing, not content. */
export const sharingKeys = ['generalAccess', 'sharedWith', 'editorsCanShare', 'viewersCanExport'] as const

/** The sharing fields only owners may change. */
export const ownerSharingKeys = ['editorsCanShare', 'viewersCanExport'] as const
